/* import __COLOCATED_TEMPLATE__ from './resistors-group.hbs'; */
import Component from '@glimmer/component';
import { service } from '@ember/service';
import { action } from '@ember/object';
import Utils from '../../../utils/utils';
import Constants from '../../../utils/constants';
import upperFirst from 'lodash.upperfirst';

export default class ResistorsGroup extends Component {
  @service intl;
  @service validationService;
  @service('aditools-analytics') analytics;

  get type() {
    return this.args.resistor.appName;
  }

  get res1Type() {
    return this.args.resistor.res1Type;
  }

  get res2Type() {
    return this.args.resistor.res2Type;
  }

  get resistorsInputId() {
    return `${this.type}-resistors-input`;
  }

  get resistorsLabel() {
    return {
      id: `${this.type}-resistors-label`,
      text: this.args.resistor.useDefaultResistorLabel
        ? this.intl.t('labels.params.resistors.default', { type: this.intl.t(`labels.parts.${this.type}`) })
        : this.intl.t(`labels.params.resistors.${this.type}`),
    };
  }

  get res1InputId() {
    return `${this.res1Type}-input`;
  }

  get res1Label() {
    return {
      id: `${this.res1Type}-label`,
      text: this.intl.t(`labels.${this.res1Type}`),
    };
  }

  get res2InputId() {
    return `${this.res2Type}-input`;
  }

  get res2Label() {
    return {
      id: `${this.res2Type}-label`,
      text: this.intl.t(`labels.${this.res2Type}`),
    };
  }

  get toleranceTempcoInputId() {
    return `${this.res1Type}-${this.res2Type}-tolerance-tempco-input`;
  }

  get toleranceTempcoLabel() {
    const res1Type = this.res1Type,
      res2Type = this.res2Type,
      hasR1andR2 = res1Type !== 'empty_string' && res2Type !== 'empty_string';

    let textId = hasR1andR2
      ? 'labels.params.resistors.tolerance_tempco'
      : 'labels.params.resistors.generic_tolerance_tempco';
    if (this.args.resistor.suppressTempCo) {
      textId = 'labels.params.resistors.just_tolerance';
    }
    return {
      id: `${this.res1Type}-${this.res2Type}-label`,
      text: this.intl.t(textId, {
        res1: this.intl.t(`labels.${this.res1Type}`),
        res2: this.intl.t(`labels.${this.res2Type}`),
      }),
    };
  }

  get isResistorDisabled() {
    const isDisabled = this.args.resistor.isDisabled; // for all (old functionality)
    const isResistorDisabled = this.args.resistor.isResistorDisabled; // for just the resistor part
    return isDisabled || isResistorDisabled;
  }

  get toleranceInputId() {
    return `${this.res1Type}-${this.res2Type}-tolerance-input`;
  }

  get toleranceLabel() {
    return {
      id: `${this.res1Type}-${this.res2Type}-tolerance-label`,
      text: this.intl.t('labels.tolerance', {
        res1: this.intl.t(`labels.${this.res1Type}`),
        res2: this.intl.t(`labels.${this.res2Type}`),
      }),
    };
  }

  get tempcoInputId() {
    return `${this.res1Type}-${this.res2Type}-tempco-input`;
  }

  get tempcoLabel() {
    return {
      id: `${this.res1Type}-${this.res2Type}-tempco`,
      text: this.intl.t('labels.tempco', {
        res1: this.intl.t(`labels.${this.res1Type}`),
        res2: this.intl.t(`labels.${this.res2Type}`),
      }),
    };
  }

  get errors() {
    const gainKey = `${this.args.resistor.propertyName}Gain`,
      res1Key = `${this.args.resistor.propertyName}Res1`,
      res2Key = `${this.args.resistor.propertyName}Res2`,
      toleranceKey = `${this.args.resistor.propertyName}Tolerance`,
      tempcoKey = `${this.args.resistor.propertyName}Tempco`,
      circuitDesignTabErrors = this.validationService.circuitDesignTabErrors;

    return {
      gain: Utils.findInItem(gainKey, circuitDesignTabErrors),
      res1: Utils.findInItem(res1Key, circuitDesignTabErrors),
      res2: Utils.findInItem(res2Key, circuitDesignTabErrors),
      tolerance: Utils.findInItem(toleranceKey, circuitDesignTabErrors),
      tempco: Utils.findInItem(tempcoKey, circuitDesignTabErrors),
    };
  }

  get messages() {
    const resistorNetworkKey = `${this.args.resistor.propertyName}NetworkPart`,
      resistorNetworkMessages = Utils.findInItem(resistorNetworkKey, this.validationService.info);

    return resistorNetworkMessages || [];
  }

  get resistorErrorsExist() {
    return this.res1ErrorsExist || this.res2ErrorsExist;
  }

  get res1ErrorsExist() {
    return this.errors && (this.errors.gain || this.errors.res1) ? true : false;
  }

  get res2ErrorsExist() {
    return this.errors && (this.errors.gain || this.errors.res2) ? true : false;
  }

  get toleranceTempcoErrorsExist() {
    return this.errors && (this.errors.tolerance || this.errors.tempco) ? true : false;
  }

  @action onResistorParamsChange(resistorId, paramName, newValue) {
    this.args.model.signalChainHandler.handleResistorParamsChange(resistorId, paramName, newValue);
    this.args.model.chartHandler.update(Constants.CHART_TYPES.ERROR_DISTRIBUTION);
    if (this.args.updateAppData) {
      this.args.updateAppData();
    }

    this.analytics.setWebEvent(
      Utils.getToolName(),
      `set${this.args.resistor.fileName}Resistor${upperFirst(paramName)}`,
      `${newValue}`,
    );
  }
}
